
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAgentConfig } from '../contexts/AgentConfigContext';
import { X, Check, Trash2 } from 'lucide-react';
import config from '../config.js';

const nextRowId = () => crypto.randomUUID();

const SecurityParamsEditor = ({ securityParams, onSubmit, onClear, isDarkMode }) => {
  const [rows, setRows] = useState(() => {
    const existing = Object.entries(securityParams || {});
    const initialRows = existing.length > 0
      ? existing.map(([key, value]) => ({ id: nextRowId(), key, value }))
      : [];
    // Always end with one empty row
    initialRows.push({ id: nextRowId(), key: '', value: '' });
    return initialRows;
  });

  const handleChange = (id, field, newValue) => {
    setRows(prev => {
      const updated = prev.map(row =>
        row.id === id ? { ...row, [field]: newValue } : row
      );
      // If the last row now has content, add a new empty row
      const lastRow = updated[updated.length - 1];
      if (lastRow.key || lastRow.value) {
        updated.push({ id: nextRowId(), key: '', value: '' });
      }
      return updated;
    });
  };

  const handleDeleteRow = (id) => {
    setRows(prev => {
      const filtered = prev.filter(row => row.id !== id);
      if (filtered.length === 0) {
        return [{ id: nextRowId(), key: '', value: '' }];
      }
      // Ensure there's always an empty row at the end
      const lastRow = filtered[filtered.length - 1];
      if (lastRow.key || lastRow.value) {
        filtered.push({ id: nextRowId(), key: '', value: '' });
      }
      return filtered;
    });
  };

  const handleApply = () => {
    const params = {};
    rows.forEach(row => {
      if (row.key.trim() && row.value.trim()) {
        params[row.key.trim()] = row.value.trim();
      }
    });
    onSubmit(params);
  };

  const handleClear = () => {
    setRows([{ id: nextRowId(), key: '', value: '' }]);
    onClear();
  };


  // Match the app's system sans-serif font (same as Tailwind's default font-sans)
  const baseFont = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  const inputStyle = {
    flex: 1,
    minWidth: 0,
    padding: '6px 10px',
    fontSize: '12px',
    borderRadius: '6px',
    border: isDarkMode ? '1px solid #4b5563' : '1px solid #d1d5db',
    backgroundColor: isDarkMode ? '#374151' : '#f9fafb',
    color: isDarkMode ? '#f3f4f6' : '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: baseFont,
  };
  const headerLabelStyle = {
    flex: 1,
    minWidth: 0,
    fontSize: '11px',
    fontWeight: 600,
    color: isDarkMode ? '#9ca3af' : '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  return (
    <div style={{
      position: 'absolute',
      right: 0,
      marginTop: '8px',
      width: '400px',
      borderRadius: '12px',
      fontFamily: baseFont,
      boxShadow: isDarkMode
        ? '0 10px 25px rgba(0,0,0,0.5)'
        : '0 10px 25px rgba(0,0,0,0.12)',
      border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
      zIndex: 1000,
      overflow: 'hidden',
    }}>

      {/* Scrollable body — header lives here too so columns always align with rows */}
      <div
        className="security-params-scroll"
        style={{
          padding: '8px 16px 12px',
          maxHeight: '260px',
          overflowY: 'auto',
          overflowX: 'hidden',
          // Reserve scrollbar gutter so columns don't shift when scrollbar appears
          scrollbarGutter: 'stable',
          // Firefox thin scrollbar with dark-mode-aware colours
          scrollbarWidth: 'thin',
          scrollbarColor: isDarkMode ? '#4b5563 transparent' : '#d1d5db transparent',
        }}
      >
        {/* Column headers — sticky so they stay visible while scrolling */}
        <div style={{
          display: 'flex',
          gap: '8px',
          paddingBottom: '6px',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
          zIndex: 1,
        }}>
          <span style={headerLabelStyle}>Key</span>
          <span style={headerLabelStyle}>Value</span>
          {/* Spacer matching the X-button column width */}
          <span style={{ width: '28px', flexShrink: 0 }} />
        </div>

        {/* Param rows */}
        {rows.map((row, index) => {
          const isLast = index === rows.length - 1;
          const isEmpty = !row.key && !row.value;
          return (
            <div key={row.id} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
              <input
                type="text"
                value={row.key}
                onChange={(e) => handleChange(row.id, 'key', e.target.value)}
                placeholder="key"
                autoComplete="off"
                style={{ ...inputStyle, opacity: isLast && isEmpty ? 0.5 : 1 }}
              />
              <input
                type="text"
                value={row.value}
                onChange={(e) => handleChange(row.id, 'value', e.target.value)}
                placeholder="value"
                autoComplete="off"
                style={{ ...inputStyle, opacity: isLast && isEmpty ? 0.5 : 1 }}
              />
              <button
                onClick={() => handleDeleteRow(row.id)}
                style={{
                  width: '28px',
                  height: '28px',
                  padding: '0',
                  borderRadius: '6px',
                  border: 'none',
                  background: 'transparent',
                  cursor: isLast && isEmpty ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isDarkMode ? '#6b7280' : '#9ca3af',
                  visibility: isLast && isEmpty ? 'hidden' : 'visible',
                  transition: 'color 0.15s',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => { if (!(isLast && isEmpty)) e.currentTarget.style.color = '#ef4444'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = isDarkMode ? '#6b7280' : '#9ca3af'; }}
                title="Remove parameter"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Footer: Clear All + Apply — equal width */}
      <div style={{
        padding: '10px 16px 14px',
        borderTop: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <button
          onClick={handleClear}
          style={{
            flex: 1,
            padding: '8px 0',
            fontSize: '12px',
            fontWeight: 600,
            borderRadius: '8px',
            border: isDarkMode ? '1px solid #4b5563' : '1px solid #d1d5db',
            backgroundColor: 'transparent',
            color: isDarkMode ? '#d1d5db' : '#374151',
            cursor: 'pointer',
            fontFamily: baseFont,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            transition: 'background-color 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#f3f4f6'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <Trash2 size={13} />
          Clear All
        </button>
        <button
          onClick={handleApply}
          style={{
            flex: 1,
            padding: '8px 0',
            fontSize: '12px',
            fontWeight: 600,
            borderRadius: '8px',
            border: 'none',
            backgroundColor: '#059669',
            color: '#ffffff',
            cursor: 'pointer',
            fontFamily: baseFont,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            transition: 'background-color 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#047857'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#059669'; }}
        >
          <Check size={14} strokeWidth={2.5} />
          Apply
        </button>
      </div>
    </div>
  );
};

SecurityParamsEditor.propTypes = {
  securityParams: PropTypes.objectOf(PropTypes.string),
  onSubmit: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  isDarkMode: PropTypes.bool.isRequired,
};

const NavBar = (props) => {
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { securityParams, setSecurityParams } = useAgentConfig();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSecurityPanel, setShowSecurityPanel] = useState(false);
  const [imageError, setImageError] = useState(false);
  const userMenuRef = useRef(null);
  const securityPanelRef = useRef(null);

  const paramCount = Object.keys(securityParams || {}).length;

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Reset image error when user changes
  useEffect(() => {
    setImageError(false);
  }, [props.user?.photoURL]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (securityPanelRef.current && !securityPanelRef.current.contains(event.target)) {
        setShowSecurityPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2050, display: 'flex', pointerEvents: 'none' }}>
      {/* Sidebar spacer - matches sidebar width */}
      <div 
        style={{ 
          width: props.isSideBar ? '260px' : '56px',
          minWidth: props.isSideBar ? '260px' : '56px',
          flexShrink: 0,
          transition: 'width 0.3s ease'
        }}
      />
      
      {/* Main navbar */}
      <nav 
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          height: '56px',
          backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
          boxShadow: 'none',
          transition: 'background-color 0.2s ease',
          pointerEvents: 'auto'
        }}
      >
         {/* Left side - Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {config.brandLogoUrl && (
            <img
              src={config.brandLogoUrl}
              alt={config.brandName}
              style={{
                height: `${32 * config.brandLogoScale}px`,
                width: 'auto',
                maxWidth: '200px',
                objectFit: 'contain',
                filter: isDarkMode ? 'invert(1) brightness(1.2)' : 'none',
                transition: 'filter 0.2s ease'
              }}
            />
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{
              fontSize: '18px',
              fontWeight: 700,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              letterSpacing: '-0.02em',
              color: isDarkMode ? '#ffffff' : '#1a1a1a',
              transition: 'color 0.2s ease',
              lineHeight: '1'
            }}>
              {config.brandName}
            </span>
            <span style={{
              fontSize: '17px',
              fontWeight: 500,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              letterSpacing: '0.02em',
              color: isDarkMode ? '#ffffff' : '#1a1a1a',
              transition: 'color 0.2s ease',
              lineHeight: '1',
              paddingLeft: '16px'
            }}>
              {config.brandTagline}
            </span>
          </div>
        </div>

        {/* Right side - Security Params, Theme Toggle and User Profile */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Security Params Button */}
          <div style={{ position: 'relative' }} ref={securityPanelRef}>
            <button
              onClick={() => setShowSecurityPanel(!showSecurityPanel)}
              style={{
                padding: '6px 12px',
                borderRadius: '20px',
                border: 'none',
                background: paramCount > 0
                  ? (isDarkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)')
                  : (isDarkMode ? 'rgba(100, 116, 139, 0.15)' : 'rgba(100, 116, 139, 0.08)'),
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                color: paramCount > 0
                  ? (isDarkMode ? '#6ee7b7' : '#059669')
                  : (isDarkMode ? '#94a3b8' : '#64748b'),
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              title={paramCount > 0
                ? `${paramCount} parameter${paramCount !== 1 ? 's' : ''} configured — click to edit`
                : 'Click to add query parameters'}
            >
              <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span style={{ fontSize: '12px', fontWeight: 600 }}>
                {paramCount > 0 ? `Security Parameters (${paramCount})` : 'Security Parameters'}
              </span>
            </button>

            {/* Security Params Editor */}
            {showSecurityPanel && (
              <SecurityParamsEditor
                securityParams={securityParams || {}}
                onSubmit={(params) => {
                  setSecurityParams(params);
                  setShowSecurityPanel(false);
                }}
                onClear={() => {
                  setSecurityParams({});
                   // Panel stays open — user may want to add new params immediately after clearing
                }}
                isDarkMode={isDarkMode}
              />
            )}
          </div>

          {/* Theme Toggle Button */}
          <button
            onClick={toggleDarkMode}
            style={{
              padding: '8px',
              borderRadius: '8px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease',
              color: isDarkMode ? '#9ca3af' : '#6b7280'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? (
              <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32l1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41m11.32-11.32l1.41-1.41" />
              </svg>
            ) : (
              <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
          </button>

          {/* User Profile with Dropdown */}
          <div style={{ position: 'relative' }} ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                paddingLeft: '8px',
                paddingRight: '12px',
                paddingTop: '6px',
                paddingBottom: '6px',
                borderRadius: '20px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                color: isDarkMode ? '#e5e7eb' : '#374151'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDarkMode ? '#1f2937' : '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* User Avatar */}
              {props.user?.photoURL && !imageError ? (
                <img 
                  src={props.user.photoURL} 
                  alt={props.user?.name || 'User'}
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                  className={`w-8 h-8 rounded-full object-cover block border-2 ${
                    isDarkMode ? 'border-slate-700' : 'border-gray-200'
                  }`}
                  onError={(e) => {
                    console.error('Image failed to load:', props.user.photoURL);
                    setImageError(true);
                  }}
                  onLoad={() => {
                  }}
                />
              ) : (
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white shrink-0 ${
                    isDarkMode ? 'bg-teal-600' : 'bg-teal-500'
                  }`}
                >
                  {getUserInitials(props.user?.name)}
                </div>
              )}
              
              {/* User Name */}
              <span className="text-sm font-medium">
                {props.user?.name}
              </span>
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  marginTop: '8px',
                  minWidth: '240px',
                  borderRadius: '12px',
                  boxShadow: isDarkMode 
                    ? '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3)' 
                    : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  overflow: 'hidden',
                  zIndex: 1000
                }}
              >
                {/* User Info Section */}
                <div style={{
                  padding: '16px',
                  borderBottom: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb'
                }}>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 600,
                    color: isDarkMode ? '#f3f4f6' : '#111827',
                    marginBottom: '4px'
                  }}>
                    {props.user?.name}
                  </div>
                  <div style={{ 
                    fontSize: '13px',
                    color: isDarkMode ? '#9ca3af' : '#6b7280'
                  }}>
                    {props.user?.email}
                  </div>
                </div>

                {/* Logout Button */}
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    props.handleLogout();
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: isDarkMode ? '#fca5a5' : '#dc2626',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};

NavBar.propTypes = {
  scrollDirection: PropTypes.number.isRequired,
  isSideBar: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string,
    photoURL: PropTypes.string,
  }),
  handleLogout: PropTypes.func.isRequired,
};

export default NavBar;
